import { useCallback, useEffect, useState, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useSession } from 'next-auth/react'

import GET_NOTIFICATION_PREFERENCES from '@app/apollo/notificationService/queries/getNotificationPreferences.graphql'
import UPSERT_FCM_TOKEN from '@app/apollo/notificationService/mutations/upsertFcmToken.graphql'
import DEACTIVATE_FCM_TOKEN_BY_TOKEN from '@app/apollo/notificationService/mutations/deactivateFcmTokenByToken.graphql'

interface FcmTokenInput {
  token: string
  deviceType?: string
  deviceInfo?: string
}

interface UseFcmTokensOptions {
  deviceType?: string
}

interface UseFcmTokensReturn {
  currentToken: string | null
  loading: boolean
  error: string | null
  pushNotificationsEnabled: boolean | null
  registerToken: () => Promise<boolean>
  deactivateCurrentToken: () => Promise<boolean>
}

const getDeviceInfo = () => {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    timestamp: new Date().toISOString(),
  }
}

export function useFcmTokens(options: UseFcmTokensOptions = {}): UseFcmTokensReturn {
  const { deviceType = 'web' } = options
  const { data: session, status } = useSession()
  const userId = session?.user?.id

  const [currentToken, setCurrentToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [upsertFcmToken] = useMutation(UPSERT_FCM_TOKEN)
  const [deactivateFcmToken] = useMutation(DEACTIVATE_FCM_TOKEN_BY_TOKEN)

  // Get user's notification preferences
  const { data: preferencesData, refetch: refetchPreferences } = useQuery(GET_NOTIFICATION_PREFERENCES, {
    variables: { userId },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  })

  // Parse push notification preference from the data
  const pushNotificationsEnabled = useMemo(() => {
    if (!preferencesData?.getNotificationPreferences?.data) {
      return null
    }

    try {
      const preferences = JSON.parse(preferencesData.getNotificationPreferences.data)
      return preferences?.pushNotifications?.enabled || false
    } catch (err) {
      console.error('Failed to parse notification preferences:', err)
      return false
    }
  }, [preferencesData])

  // Register FCM token
  const registerToken = useCallback(async (): Promise<boolean> => {
    if (!userId) {
      setError('User not authenticated')
      return false
    }

    if (!pushNotificationsEnabled) {
      console.log('Push notifications not enabled in user preferences')
      return false
    }

    try {
      setLoading(true)
      setError(null)

      // Get FCM token from Firebase
      const { requestPermissionAndGetToken } = await import('@app/utils/firebase')
      const token = await requestPermissionAndGetToken()
      if (!token) {
        setError('Failed to get FCM token or permission denied')
        return false
      }

      // Send to server
      const input: FcmTokenInput = {
        token,
        deviceType,
        deviceInfo: JSON.stringify(getDeviceInfo()),
      }

      console.log('Registering FCM token:', input)

      const { data } = await upsertFcmToken({
        variables: { userId, input },
      })

      console.log('Response from upsertFcmToken:', data)

      if (data?.upsertFcmToken?.success) {
        setCurrentToken(token)
        console.log('FCM token registered successfully')
        return true
      } else {
        const errorMsg = data?.upsertFcmToken?.message || 'Failed to register FCM token'
        setError(errorMsg)
        return false
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to register FCM token'
      setError(errorMsg)
      return false
    } finally {
      setLoading(false)
    }
  }, [userId, pushNotificationsEnabled, deviceType, upsertFcmToken])

  // Deactivate current token (for logout or when user disables push notifications)
  const deactivateCurrentToken = useCallback(async (): Promise<boolean> => {
    console.log('i am inside the deactivate function', currentToken)
    if (!currentToken) {
      return true // Nothing to deactivate
    }

    try {
      setLoading(true)

      const { data } = await deactivateFcmToken({
        variables: { token: currentToken },
      })

      if (data?.deactivateFcmTokenByToken?.success) {
        setCurrentToken(null)
        console.log('FCM token deactivated successfully')
        return true
      } else {
        console.error('Failed to deactivate FCM token:', data?.deactivateFcmTokenByToken?.message)
        return false
      }
    } catch (err) {
      console.error('Error deactivating FCM token:', err)
      return false
    } finally {
      setLoading(false)
    }
  }, [currentToken, deactivateFcmToken])

  // Auto-register when user logs in AND has push notifications enabled
  useEffect(() => {
    if (status === 'authenticated' && userId && pushNotificationsEnabled && !currentToken) {
      registerToken()
    }
  }, [status, userId, pushNotificationsEnabled, currentToken, registerToken])

  // Handle push notification preference changes
  useEffect(() => {
    if (status === 'authenticated' && userId) {
      if (pushNotificationsEnabled && !currentToken) {
        // User enabled push notifications - register token
        registerToken()
      } else if (pushNotificationsEnabled === false && currentToken) {
        // User disabled push notifications - deactivate token
        deactivateCurrentToken()
      }
    }
  }, [status, userId, pushNotificationsEnabled, currentToken, registerToken, deactivateCurrentToken])

  // Auto-deactivate when user logs out
  useEffect(() => {
    if (status === 'unauthenticated' && currentToken) {
      deactivateCurrentToken()
    }
  }, [status, currentToken, deactivateCurrentToken])

  return {
    currentToken,
    loading,
    error,
    pushNotificationsEnabled,
    registerToken,
    deactivateCurrentToken,
  }
}
